import React, { useEffect, useState } from "react";
import { DashboardLayout } from "../../components/DashboardLayout";
import { EmptyState, CommonModal } from "../../components/Common";
import api from "../../lib/api";
import { toast } from "sonner";
import { Globe, Palette, LayoutTemplate, FileText, Settings, Upload, Trash2, Plus, MessageSquare, Star, ExternalLink, Activity, Pencil, Image as ImageIcon } from "lucide-react";
import { links as clinicLinks } from "./ClinicAdminPages";

export function ClinicWebsiteCMS() {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoadError("");
    try {
      const res = await api.get("/clinic/website");
      setData(res.data);
    } catch (e) {
      setLoadError(e?.response?.data?.detail || "Failed to load website settings");
      toast.error("Failed to load website settings");
    }
  };

  const publishWebsite = async () => {
    try {
      await api.post("/clinic/website/publish");
      toast.success("Website Published successfully!");
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    }
  };

  if (loadError) return <DashboardLayout links={clinicLinks} title="Website CMS"><div className="mg-card text-center"><p className="text-red-600">{loadError}</p><button className="mg-btn-secondary mt-3" onClick={fetchData}>Retry</button></div></DashboardLayout>;
  if (!data) return <DashboardLayout links={clinicLinks} title="Website CMS"><div className="p-8 text-center text-slate-500">Loading...</div></DashboardLayout>;

  return (
    <DashboardLayout links={clinicLinks} title="Website CMS">
      <div className="flex items-center gap-1 overflow-x-auto pb-2 border-b border-slate-200 mb-6 custom-scrollbar">
        <TabBtn label="Overview" active={activeTab === "overview"} onClick={() => setActiveTab("overview")} icon={<Globe />} />
        {data.has_advanced_feature && (
           <>
             <TabBtn label="Theme" active={activeTab === "theme"} onClick={() => setActiveTab("theme")} icon={<Palette />} />
             <TabBtn label="Pages" active={activeTab === "pages"} onClick={() => setActiveTab("pages")} icon={<LayoutTemplate />} />
             <TabBtn label="Gallery" active={activeTab === "gallery"} onClick={() => setActiveTab("gallery")} icon={<ImageIcon />} />
             <TabBtn label="FAQs" active={activeTab === "faqs"} onClick={() => setActiveTab("faqs")} icon={<MessageSquare />} />
             <TabBtn label="Testimonials" active={activeTab === "testimonials"} onClick={() => setActiveTab("testimonials")} icon={<Star />} />
           </>
        )}
      </div>

      <div className="min-h-[400px]">
        {activeTab === "overview" && <OverviewTab data={data} onPublish={publishWebsite} onSave={fetchData} />}
        {data.has_advanced_feature && (
           <>
             {activeTab === "theme" && <ThemeTab data={data} onSave={fetchData} />}
             {activeTab === "pages" && <PagesTab />}
             {activeTab === "gallery" && <GalleryTab />}
             {activeTab === "faqs" && <FaqsTab />}
             {activeTab === "testimonials" && <TestimonialsTab />}
           </>
        )}
      </div>
    </DashboardLayout>
  );
}

function TabBtn({ label, active, onClick, icon }) {
  return (
    <button onClick={onClick} className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-t-lg transition-colors whitespace-nowrap ${active ? "bg-mg-medical text-white" : "text-slate-600 hover:bg-slate-100"}`}>
      {React.cloneElement(icon, { className: "w-4 h-4" })} {label}
    </button>
  );
}

function OverviewTab({ data, onPublish, onSave }) {
  const isAdvanced = data.has_advanced_feature;
  const web = data.website;
  const [seo, setSeo] = useState({ seo_title: web.seo_title || "", seo_description: web.seo_description || "" });
  const [savingSeo, setSavingSeo] = useState(false);

  const saveSettings = async (updates) => {
     try {
       await api.patch("/clinic/website", updates);
       toast.success("Settings saved");
       onSave && onSave();
       return true;
     } catch (e) {
       toast.error(e?.response?.data?.detail || "Failed to save settings");
       return false;
     }
  };

  const saveSeo = async () => {
    setSavingSeo(true);
    await saveSettings(seo);
    setSavingSeo(false);
  };

  return (
    <div className="space-y-6">
       {!isAdvanced && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex items-start gap-4">
             <div className="bg-amber-100 text-amber-600 p-2 rounded-lg">
                <Settings className="w-5 h-5" />
             </div>
             <div>
                <h3 className="font-bold text-amber-800">Advanced Website Locked</h3>
                <p className="text-sm text-amber-700 mt-1">Your current plan only includes the basic booking page. Upgrade your plan to unlock full multi-page dynamic websites, theme customization, SEO, galleries, and more.</p>
             </div>
          </div>
       )}

       <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
         <div className="mg-card space-y-4">
           <h3 className="font-bold text-mg-navy">Status & URLs</h3>
           <div className="flex justify-between items-center bg-slate-50 p-3 rounded-lg border border-slate-100">
              <span className="text-sm text-slate-600 font-semibold">Website Status</span>
              <span className={`mg-status mg-status-${web.status}`}>{web.status}</span>
           </div>
           <div className="flex justify-between items-center bg-slate-50 p-3 rounded-lg border border-slate-100">
              <span className="text-sm text-slate-600 font-semibold">Current Mode</span>
              <span className="mg-status mg-status-active">{web.mode}</span>
           </div>

           {isAdvanced && (
              <div className="flex gap-2 pt-4">
                 {web.status !== "published" && (
                    <button onClick={onPublish} className="mg-btn-primary flex-1" data-testid="publish-website-btn">Publish Advanced Website</button>
                 )}
                 {web.status === "published" && (
                    <button onClick={() => saveSettings({status: "unpublished"})} className="mg-btn-secondary border-red-200 text-red-600 flex-1 hover:bg-red-50" data-testid="unpublish-website-btn">Unpublish</button>
                 )}
              </div>
           )}
         </div>

         <div className="mg-card">
           <h3 className="font-bold text-mg-navy mb-4">SEO Settings</h3>
           <div className={`space-y-3 ${isAdvanced ? "" : "opacity-50"}`}>
             <label className="text-xs text-slate-500 block mb-1">Site Title</label>
             <input className="mg-input w-full" disabled={!isAdvanced} value={seo.seo_title} onChange={(e) => setSeo({ ...seo, seo_title: e.target.value })} placeholder="Site Title" data-testid="seo-title-input" />

             <label className="text-xs text-slate-500 block mb-1">Meta Description</label>
             <textarea className="mg-input w-full" disabled={!isAdvanced} rows={2} value={seo.seo_description} onChange={(e) => setSeo({ ...seo, seo_description: e.target.value })} placeholder="Description" data-testid="seo-description-input" />
           </div>
           {isAdvanced && (
             <button onClick={saveSeo} disabled={savingSeo} className="mg-btn-primary text-sm mt-3" data-testid="save-seo-btn">{savingSeo ? "Saving…" : "Save SEO"}</button>
           )}
         </div>
       </div>
    </div>
  );
}

function ThemeTab({ data, onSave }) {
  const [form, setForm] = useState({
    theme_preset: data.website.theme_preset || "Medical Blue",
    primary_color: data.website.primary_color || "#0EA5A4",
    secondary_color: data.website.secondary_color || "#0F4C5C",
    accent_color: data.website.accent_color || "#F4B400",
    background_color: data.website.background_color || "#FFFFFF",
    text_color: data.website.text_color || "#1E293B",
    button_style: data.website.button_style || "rounded",
    font_style: data.website.font_style || "inter"
  });

  const saveTheme = async () => {
    try {
      await api.patch("/clinic/website", form);
      toast.success("Theme updated");
      onSave();
    } catch(e) { toast.error("Failed to update theme"); }
  };

  const presets = [
    { name: "Medical Blue", primary: "#0EA5A4", sec: "#0F4C5C", acc: "#F4B400" },
    { name: "Green Wellness", primary: "#10B981", sec: "#064E3B", acc: "#34D399" },
    { name: "Minimal White", primary: "#111827", sec: "#374151", acc: "#6366F1" },
    { name: "Pediatric Friendly", primary: "#F472B6", sec: "#BE185D", acc: "#FBBF24" }
  ];

  const applyPreset = (p) => {
    setForm({...form, theme_preset: p.name, primary_color: p.primary, secondary_color: p.sec, accent_color: p.acc});
  };

  return (
    <div className="mg-card">
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-bold text-mg-navy">Theme Customization</h3>
        <button onClick={saveTheme} className="mg-btn-primary text-sm">Save Theme</button>
      </div>

      <div className="mb-6">
        <label className="text-sm font-bold text-slate-700 block mb-3">Quick Presets</label>
        <div className="flex gap-4 overflow-x-auto pb-2">
           {presets.map(p => (
              <button key={p.name} onClick={() => applyPreset(p)} className={`flex items-center gap-3 p-3 border rounded-xl min-w-[200px] hover:border-mg-medical transition-all ${form.theme_preset === p.name ? "border-mg-medical bg-mg-medical/5" : "border-slate-200"}`}>
                 <div className="flex">
                    <div className="w-6 h-6 rounded-l-full" style={{backgroundColor: p.primary}}></div>
                    <div className="w-6 h-6" style={{backgroundColor: p.sec}}></div>
                    <div className="w-6 h-6 rounded-r-full" style={{backgroundColor: p.acc}}></div>
                 </div>
                 <span className="text-sm font-semibold">{p.name}</span>
              </button>
           ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
           <div>
             <label className="text-xs text-slate-500 block mb-1">Primary Color</label>
             <div className="flex gap-2"><input type="color" className="h-10 w-12 rounded border border-slate-200" value={form.primary_color} onChange={e=>setForm({...form, primary_color: e.target.value})} /><input className="mg-input flex-1 uppercase" value={form.primary_color} onChange={e=>setForm({...form, primary_color: e.target.value})} /></div>
           </div>
           <div>
             <label className="text-xs text-slate-500 block mb-1">Secondary Color</label>
             <div className="flex gap-2"><input type="color" className="h-10 w-12 rounded border border-slate-200" value={form.secondary_color} onChange={e=>setForm({...form, secondary_color: e.target.value})} /><input className="mg-input flex-1 uppercase" value={form.secondary_color} onChange={e=>setForm({...form, secondary_color: e.target.value})} /></div>
           </div>
           <div>
             <label className="text-xs text-slate-500 block mb-1">Accent Color</label>
             <div className="flex gap-2"><input type="color" className="h-10 w-12 rounded border border-slate-200" value={form.accent_color} onChange={e=>setForm({...form, accent_color: e.target.value})} /><input className="mg-input flex-1 uppercase" value={form.accent_color} onChange={e=>setForm({...form, accent_color: e.target.value})} /></div>
           </div>
        </div>
        <div className="space-y-4">
           <div>
             <label className="text-xs text-slate-500 block mb-1">Button Style</label>
             <select className="mg-input w-full" value={form.button_style} onChange={e=>setForm({...form, button_style: e.target.value})}>
                <option value="rounded">Rounded (Default)</option>
                <option value="sharp">Sharp / Square</option>
                <option value="pill">Pill</option>
             </select>
           </div>
           <div>
             <label className="text-xs text-slate-500 block mb-1">Typography / Font Style</label>
             <select className="mg-input w-full" value={form.font_style} onChange={e=>setForm({...form, font_style: e.target.value})}>
                <option value="inter">Inter (Clean, Modern)</option>
                <option value="roboto">Roboto</option>
                <option value="poppins">Poppins</option>
                <option value="serif">Playfair Display (Elegant Serif)</option>
             </select>
           </div>
        </div>
      </div>
    </div>
  );
}

function PagesTab() {
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null); // page object being edited, or null
  useEffect(() => { loadPages(); }, []);
  const loadPages = () => api.get("/clinic/website/pages").then(r => setPages(r.data));

  const togglePage = async (page) => {
     try {
       await api.patch(`/clinic/website/pages/${page.page_id}`, { is_enabled: !page.is_enabled });
       loadPages();
     } catch(e) {}
  };

  const deletePage = async (page) => {
    if (!window.confirm(`Delete the "${page.title}" page? This removes it and its content permanently.`)) return;
    try {
      await api.delete(`/clinic/website/pages/${page.page_id}`);
      toast.success("Page deleted");
      loadPages();
    } catch(e) { toast.error(e?.response?.data?.detail || "Failed to delete page"); }
  };

  const autoSetup = async () => {
    const keys = ["home", "about", "doctors", "services", "contact", "book", "faq", "testimonials"];
    for(let k of keys) {
      await api.post("/clinic/website/pages", { page_key: k, title: k.charAt(0).toUpperCase() + k.slice(1), slug: k, is_enabled: true });
    }
    loadPages();
    toast.success("Default pages created");
  };

  if (pages.length === 0) {
    return <EmptyState title="No Pages Configured" message="Generate default website pages to get started." action={<button onClick={autoSetup} className="mg-btn-primary">Generate Default Pages</button>} />
  }

  return (
    <>
      <div className="mg-card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase text-slate-500">
            <tr><th className="px-6 py-4">Page</th><th>Slug</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {pages.map(p => (
              <tr key={p.page_id} className="hover:bg-slate-50" data-testid={`page-row-${p.page_key}`}>
                <td className="px-6 py-4 font-bold text-mg-navy">{p.title}</td>
                <td className="px-6 py-4 font-mono text-xs text-slate-500">/{p.slug === "home" ? "" : p.slug}</td>
                <td className="px-6 py-4">
                  <span className={`mg-status ${p.is_enabled ? 'mg-status-active' : 'mg-status-inactive'}`}>{p.is_enabled ? 'Visible' : 'Hidden'}</span>
                </td>
                <td className="px-6 py-4 flex gap-3 items-center">
                  <button onClick={() => setEditing(p)} className="text-xs text-mg-primary hover:underline flex items-center gap-1 font-semibold" data-testid={`edit-page-${p.page_key}`}><Pencil className="w-3.5 h-3.5" /> Edit content</button>
                  <button onClick={() => togglePage(p)} className="text-xs text-slate-500 hover:text-mg-navy underline" data-testid={`toggle-page-${p.page_key}`}>{p.is_enabled ? "Hide" : "Show"}</button>
                  <button onClick={() => deletePage(p)} className="text-xs text-red-500 hover:text-red-700" data-testid={`delete-page-${p.page_key}`} aria-label={`Delete ${p.title || p.page_key} page`}><Trash2 className="w-3.5 h-3.5" aria-hidden="true" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <PageContentModal page={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); loadPages(); }} />}
    </>
  );
}

function PageContentModal({ page, onClose, onSaved }) {
  const c = page.content_json || {};
  const isHome = page.page_key === "home";
  const isAbout = page.page_key === "about";
  const [form, setForm] = useState({
    heading: c.heading || "",
    subheading: c.subheading || "",
    body: c.body || "",
    hero_badge_text: c.hero_badge_text || "",
    hero_title: c.hero_title || "",
    hero_subtitle: c.hero_subtitle || "",
    cta_primary_label: c.cta_primary_label || "",
    cta_secondary_label: c.cta_secondary_label || "",
    stat_years_experience: c.stat_years_experience || "",
    stat_happy_patients: c.stat_happy_patients || "",
  });
  const [values, setValues] = useState(c.values && c.values.length ? c.values : [
    { title: "Patient-Centered Care", desc: "" },
    { title: "Advanced Technology", desc: "" },
    { title: "Expert Team", desc: "" },
    { title: "Compassionate Support", desc: "" },
  ]);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setValue = (i, k, v) => setValues((vs) => vs.map((item, idx) => idx === i ? { ...item, [k]: v } : item));

  const save = async () => {
    setSaving(true);
    try {
      const content_json = { ...form };
      if (isAbout) content_json.values = values;
      await api.patch(`/clinic/website/pages/${page.page_id}`, { content_json });
      toast.success("Page content saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <CommonModal title={`Edit content — ${page.title}`} onClose={onClose} onSave={save}>
      <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
        {isHome && (
          <>
            <Field label="Hero badge text" value={form.hero_badge_text} onChange={(v) => set("hero_badge_text", v)} placeholder="e.g. Welcome to MediGenie Clinic" testid="page-content-hero_badge_text" />
            <Field label="Hero title" value={form.hero_title} onChange={(v) => set("hero_title", v)} placeholder="Advanced Healthcare Tailored for You" testid="page-content-hero_title" />
            <Field label="Hero subtitle" value={form.hero_subtitle} onChange={(v) => set("hero_subtitle", v)} textarea placeholder="A short description shown under the hero title" testid="page-content-hero_subtitle" />
            <Field label="Primary button label" value={form.cta_primary_label} onChange={(v) => set("cta_primary_label", v)} placeholder="Book Appointment" testid="page-content-cta_primary_label" />
            <Field label="Secondary button label" value={form.cta_secondary_label} onChange={(v) => set("cta_secondary_label", v)} placeholder="Our Services" testid="page-content-cta_secondary_label" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Years of experience" value={form.stat_years_experience} onChange={(v) => set("stat_years_experience", v)} placeholder="15+" testid="page-content-stat_years" />
              <Field label="Happy patients" value={form.stat_happy_patients} onChange={(v) => set("stat_happy_patients", v)} placeholder="10k+" testid="page-content-stat_patients" />
            </div>
          </>
        )}
        {isAbout && (
          <>
            <Field label="Section heading" value={form.heading} onChange={(v) => set("heading", v)} placeholder="Why Choose Us" testid="page-content-heading" />
            <Field label="Section subheading" value={form.subheading} onChange={(v) => set("subheading", v)} placeholder="Our Core Values" testid="page-content-subheading" />
            <Field label="About body text" value={form.body} onChange={(v) => set("body", v)} textarea placeholder="Tell visitors about your clinic" testid="page-content-body" />
            <label className="text-xs text-slate-500 block mt-2">Core value cards</label>
            {values.map((v, i) => (
              <div key={i} className="border border-slate-200 rounded-lg p-2 space-y-1">
                <input className="mg-input w-full text-sm" value={v.title} onChange={(e) => setValue(i, "title", e.target.value)} placeholder="Value title" data-testid={`page-content-value-title-${i}`} />
                <input className="mg-input w-full text-sm" value={v.desc} onChange={(e) => setValue(i, "desc", e.target.value)} placeholder="Value description" data-testid={`page-content-value-desc-${i}`} />
              </div>
            ))}
          </>
        )}
        {!isHome && !isAbout && (
          <>
            <Field label="Section heading" value={form.heading} onChange={(v) => set("heading", v)} placeholder={page.title} testid="page-content-heading" />
            <Field label="Section subheading" value={form.subheading} onChange={(v) => set("subheading", v)} textarea placeholder="A short tagline shown under the heading" testid="page-content-subheading" />
          </>
        )}
      </div>
    </CommonModal>
  );
}

function Field({ label, value, onChange, placeholder, textarea, testid }) {
  return (
    <div>
      <label className="text-xs text-slate-500 block mb-1">{label}</label>
      {textarea ? (
        <textarea className="mg-input w-full" rows={2} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} />
      ) : (
        <input className="mg-input w-full" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} />
      )}
    </div>
  );
}

function GalleryTab() {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  useEffect(() => { load(); }, []);
  const load = () => api.get("/clinic/website/gallery").then(r => setImages(r.data));

  const onFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image too large (max 5MB)"); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post("/clinic/website/gallery", { data_url: reader.result, caption: "" });
        toast.success("Image uploaded");
        load();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Upload failed");
      } finally { setUploading(false); e.target.value = ""; }
    };
    reader.readAsDataURL(file);
  };

  const updateCaption = async (img, caption) => {
    try { await api.patch(`/clinic/website/gallery/${img.image_id}`, { caption }); }
    catch (e) {}
  };

  const remove = async (img) => {
    if (!window.confirm("Delete this image from the gallery?")) return;
    try {
      await api.delete(`/clinic/website/gallery/${img.image_id}`);
      toast.success("Image deleted");
      load();
    } catch (e) { toast.error("Failed to delete image"); }
  };

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  return (
    <div className="space-y-4">
      <div className="mg-card flex items-center justify-between">
        <div>
          <h3 className="font-bold text-mg-navy">Clinic Gallery</h3>
          <p className="text-xs text-slate-500 mt-1">Photos shown in the Gallery section of your public website. Max 5MB per image.</p>
        </div>
        <label className="mg-btn-primary text-sm cursor-pointer" data-testid="upload-gallery-image-btn">
          <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload photo"}
          <input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={uploading} data-testid="gallery-file-input" />
        </label>
      </div>
      {images.length === 0 ? (
        <EmptyState title="No photos yet" message="Upload clinic photos to showcase on your public website." />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {images.map((img) => (
            <div key={img.image_id} className="mg-card p-2 space-y-2" data-testid={`gallery-image-${img.image_id}`}>
              <img src={`${API_BASE}${img.image_path}`} alt={img.caption || "Gallery"} className="w-full h-28 object-cover rounded-lg" />
              <input
                className="mg-input w-full text-xs"
                defaultValue={img.caption}
                placeholder="Caption"
                onBlur={(e) => updateCaption(img, e.target.value)}
                data-testid={`gallery-caption-${img.image_id}`}
              />
              <button onClick={() => remove(img)} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1" data-testid={`delete-gallery-image-${img.image_id}`}>
                <Trash2 className="w-3.5 h-3.5" /> Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FaqsTab() {
  const [faqs, setFaqs] = useState([]);
  const [modal, setModal] = useState(null); // "new" | faq object | null
  useEffect(() => { load(); }, []);
  const load = () => api.get("/clinic/website/faqs").then(r => setFaqs(r.data));

  const remove = async (f) => {
    if (!window.confirm(`Delete FAQ "${f.question}"?`)) return;
    try { await api.delete(`/clinic/website/faqs/${f.faq_id}`); toast.success("FAQ deleted"); load(); }
    catch(e) { toast.error("Failed to delete FAQ"); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-bold text-mg-navy">Manage FAQs</h3>
        <button onClick={() => setModal("new")} className="mg-btn-primary text-sm flex items-center gap-1" data-testid="add-faq-btn"><Plus className="w-4 h-4"/> Add FAQ</button>
      </div>
      <div className="space-y-3">
         {faqs.map(f => (
           <div key={f.faq_id} className="mg-card p-4" data-testid={`faq-row-${f.faq_id}`}>
             <div className="flex items-start justify-between gap-3">
               <div className="flex-1">
                 <div className="font-bold text-mg-navy mb-1">{f.question}</div>
                 <div className="text-sm text-slate-600">{f.answer}</div>
               </div>
               <div className="flex gap-2 shrink-0">
                 <button onClick={() => setModal(f)} className="text-mg-primary hover:text-mg-navy" data-testid={`edit-faq-${f.faq_id}`}><Pencil className="w-4 h-4" /></button>
                 <button onClick={() => remove(f)} className="text-red-500 hover:text-red-700" data-testid={`delete-faq-${f.faq_id}`} aria-label="Delete FAQ"><Trash2 className="w-4 h-4" aria-hidden="true" /></button>
               </div>
             </div>
           </div>
         ))}
         {faqs.length === 0 && <EmptyState title="No FAQs added" />}
      </div>
      {modal && <FaqModal existing={modal === "new" ? null : modal} onClose={() => setModal(null)} onSave={() => { setModal(null); load(); }} />}
    </div>
  );
}

function FaqModal({ existing, onClose, onSave }) {
  const [form, setForm] = useState({ question: existing?.question || "", answer: existing?.answer || "" });
  const save = async () => {
    try {
      if (existing) {
        await api.patch(`/clinic/website/faqs/${existing.faq_id}`, { ...form, is_enabled: existing.is_enabled ?? true });
        toast.success("FAQ updated");
      } else {
        await api.post("/clinic/website/faqs", form);
        toast.success("FAQ added");
      }
      onSave();
    } catch(e) { toast.error("Failed"); }
  };
  return (
    <CommonModal title={existing ? "Edit FAQ" : "Add FAQ"} onClose={onClose} onSave={save}>
       <div className="space-y-4">
          <div><label className="text-xs font-semibold text-slate-500 block mb-1">Question</label><input className="mg-input w-full" value={form.question} onChange={e=>setForm({...form, question: e.target.value})} data-testid="faq-question-input" /></div>
          <div><label className="text-xs font-semibold text-slate-500 block mb-1">Answer</label><textarea className="mg-input w-full min-h-[100px]" value={form.answer} onChange={e=>setForm({...form, answer: e.target.value})} data-testid="faq-answer-input" /></div>
       </div>
    </CommonModal>
  );
}

function TestimonialsTab() {
  const [testimonials, setTestimonials] = useState([]);
  const [modal, setModal] = useState(null); // "new" | testimonial object | null
  useEffect(() => { load(); }, []);
  const load = () => api.get("/clinic/website/testimonials").then(r => setTestimonials(r.data));

  const remove = async (t) => {
    if (!window.confirm(`Delete testimonial from "${t.patient_display_name}"?`)) return;
    try { await api.delete(`/clinic/website/testimonials/${t.testimonial_id}`); toast.success("Testimonial deleted"); load(); }
    catch(e) { toast.error("Failed to delete testimonial"); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-bold text-mg-navy">Manage Testimonials</h3>
        <button onClick={() => setModal("new")} className="mg-btn-primary text-sm flex items-center gap-1" data-testid="add-testimonial-btn"><Plus className="w-4 h-4"/> Add Testimonial</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
         {testimonials.map(t => (
           <div key={t.testimonial_id} className="mg-card p-5" data-testid={`testimonial-row-${t.testimonial_id}`}>
             <div className="flex items-start justify-between">
               <div className="flex gap-1 mb-2 text-mg-gold">
                 {[...Array(t.rating || 5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
               </div>
               <div className="flex gap-2 shrink-0">
                 <button onClick={() => setModal(t)} className="text-mg-primary hover:text-mg-navy" data-testid={`edit-testimonial-${t.testimonial_id}`}><Pencil className="w-4 h-4" /></button>
                 <button onClick={() => remove(t)} className="text-red-500 hover:text-red-700" data-testid={`delete-testimonial-${t.testimonial_id}`} aria-label="Delete testimonial"><Trash2 className="w-4 h-4" aria-hidden="true" /></button>
               </div>
             </div>
             <p className="text-sm text-slate-700 italic mb-4">"{t.comment}"</p>
             <div className="font-bold text-mg-navy text-sm">— {t.patient_display_name}</div>
           </div>
         ))}
         {testimonials.length === 0 && <EmptyState title="No Testimonials added" />}
      </div>
      {modal && <TestimonialModal existing={modal === "new" ? null : modal} onClose={() => setModal(null)} onSave={() => { setModal(null); load(); }} />}
    </div>
  );
}

function TestimonialModal({ existing, onClose, onSave }) {
  const [form, setForm] = useState({
    patient_display_name: existing?.patient_display_name || "",
    comment: existing?.comment || "",
    rating: existing?.rating || 5,
  });
  const save = async () => {
    try {
      if (existing) {
        await api.patch(`/clinic/website/testimonials/${existing.testimonial_id}`, { ...form, is_published: existing.is_published ?? true });
        toast.success("Testimonial updated");
      } else {
        await api.post("/clinic/website/testimonials", form);
        toast.success("Testimonial added");
      }
      onSave();
    } catch(e) { toast.error("Failed"); }
  };
  return (
    <CommonModal title={existing ? "Edit Testimonial" : "Add Testimonial"} onClose={onClose} onSave={save}>
       <div className="space-y-4">
          <div><label className="text-xs font-semibold text-slate-500 block mb-1">Patient Name</label><input className="mg-input w-full" value={form.patient_display_name} onChange={e=>setForm({...form, patient_display_name: e.target.value})} data-testid="testimonial-name-input" /></div>
          <div><label className="text-xs font-semibold text-slate-500 block mb-1">Rating (1-5)</label><input type="number" min="1" max="5" className="mg-input w-full" value={form.rating} onChange={e=>setForm({...form, rating: Number(e.target.value)})} data-testid="testimonial-rating-input" /></div>
          <div><label className="text-xs font-semibold text-slate-500 block mb-1">Review</label><textarea className="mg-input w-full min-h-[100px]" value={form.comment} onChange={e=>setForm({...form, comment: e.target.value})} data-testid="testimonial-comment-input" /></div>
       </div>
    </CommonModal>
  );
}
